import { useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { arrayMove } from '@dnd-kit/sortable';
import { productsService, type ProductView } from '../../../../../services/products/products.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import { sortBySortOrder } from './product-filter-sort.utils';

type UseProductReorderControllerArgs = {
  products: ProductView[];
  visibleIds: number[];
  employeeSession: string | null;
  enabled: boolean;
};

// Architectural summary: splice a visible-list move into full global order; hidden rows unmoved.
function moveWithinFullOrder(
  ordered: ProductView[],
  visibleIds: number[],
  activeId: number,
  overId: number,
): ProductView[] | null {
  const from = visibleIds.indexOf(activeId);
  const to = visibleIds.indexOf(overId);
  if (from < 0 || to < 0) return null;
  const active = ordered.find((product) => product.id === activeId);
  if (!active) return null;
  const newVisible = arrayMove(visibleIds, from, to);
  const pos = newVisible.indexOf(activeId);
  const remaining = ordered.filter((product) => product.id !== activeId);
  // Insert before the new next visible neighbor, or after the previous one when dropped at the end.
  const insertIdx = pos < newVisible.length - 1
    ? remaining.findIndex((product) => product.id === newVisible[pos + 1])
    : remaining.findIndex((product) => product.id === newVisible[pos - 1]) + 1;
  if (insertIdx < 0) return null;
  remaining.splice(insertIdx, 0, active);
  return remaining;
}

// Architectural summary: Optimistic catalog drag-reorder; reverts local order on API failure.
export function useProductReorderController({ products, visibleIds, employeeSession, enabled }: UseProductReorderControllerArgs) {
  const base = useMemo(() => sortBySortOrder(products), [products]);
  // Optimistic state holds ONLY the id order; the rendered list is derived from current props.
  const [orderedIds, setOrderedIds] = useState<number[]>(() => base.map((product) => product.id));
  const inFlightRef = useRef(false);

  // Re-seed the id order whenever the persisted order changes (SSE echo, edits, activation).
  const signature = base.map((product) => `${product.id}:${product.sortOrder}`).join(',');
  useEffect(() => {
    if (!inFlightRef.current) {
      setOrderedIds(base.map((product) => product.id));
    }
    // signature is the stable scalar key for the persisted order (Rule 21).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // List derived from current props via the id order, so content edits show (mistake #86).
  const ordered = useMemo(() => {
    const byId = new Map(base.map((product) => [product.id, product]));
    const seen = new Set<number>();
    const result: ProductView[] = [];
    for (const id of orderedIds) {
      const product = byId.get(id);
      if (product) { result.push(product); seen.add(id); }
    }
    for (const product of base) {
      if (!seen.has(product.id)) result.push(product);
    }
    return result;
  }, [base, orderedIds]);

  // Visible subset in global order — the list shown (and reordered) under an active filter/search.
  const visibleSignature = visibleIds.join(',');
  const orderedVisible = useMemo(
    () => {
      const visibleSet = new Set(visibleIds);
      return ordered.filter((product) => visibleSet.has(product.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordered, visibleSignature],
  );

  const handleDragEnd = async (activeId: number, overId: number) => {
    if (!enabled || !employeeSession || inFlightRef.current || activeId === overId) return;
    const next = moveWithinFullOrder(ordered, orderedVisible.map((product) => product.id), activeId, overId);
    if (!next) return;

    const previous = orderedIds;
    const nextIds = next.map((product) => product.id);
    setOrderedIds(nextIds);
    inFlightRef.current = true;
    try {
      await productsService.reorder(nextIds, employeeSession);
    } catch (error) {
      setOrderedIds(previous);
      if (error instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
      }
    } finally {
      inFlightRef.current = false;
    }
  };

  return { ordered, orderedVisible, handleDragEnd };
}
